import { Box, RadioButton, RadioGroup, Stack } from '@eneco/sparky';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';

import type { EnergyUsagePurposeOption } from './address-finder-content.types';

interface Props<TFieldValues extends FieldValues> {
  readonly control: Control<TFieldValues>;
  readonly label: string;
  readonly options: EnergyUsagePurposeOption[];
  readonly direction?: 'row' | 'column';
}

/**
 * The "Huishouden of Zakelijk" radio group shared by `AddressFinder` and `SmeAddressFinder` — ported
 * inline from the legacy `EnergyUsagePurposeSelector`
 * (`libs/sitecore/components/content/src/AddressFinder/EnergyUsagePurpose/EnergyUsagePurposeSelector.tsx`),
 * which lives in `lib:sitecore-components`, unreachable from this lane's module boundary.
 *
 * Known simplification: `options` has no resolvable content on `?_cms=sanity` yet, so this renders an
 * empty group there until `RadioGroupField.options`'s `migrationUnresolvedReference` gets a real target
 * type (see `address-finder-content.types.ts`); the Sitecore lane's options are unaffected.
 *
 * Generic over the consuming form's field values so both blocks' distinct `FormProps` shapes — which
 * only share the one `energyUsagePurpose` field this selector actually controls — type-check against it.
 */
export function EnergyUsagePurposeSelector<TFieldValues extends FieldValues>({
  control,
  label,
  options,
  direction = 'row',
}: Props<TFieldValues>) {
  return (
    <Box paddingTop="3">
      <Stack direction="column" gap="3">
        <Controller
          control={control}
          name={'energyUsagePurpose' as Path<TFieldValues>}
          render={({ field: { onChange, value, name }, fieldState: { error } }) => (
            <RadioGroup
              name={name}
              direction={direction}
              value={(value as string) ?? ''}
              onValueChange={onChange as (value: string) => void}
              label={label}
              error={error?.message}>
              {options.map(option => (
                <RadioButton value={option.name} key={option.name}>
                  {option.label}
                </RadioButton>
              ))}
            </RadioGroup>
          )}
        />
      </Stack>
    </Box>
  );
}
